/**
 * PlacementTableRowExpanded - Expanded row content showing details
 *
 * Displays additional information when a table row is expanded:
 * - Segment breakdown for multi-move placements
 * - Decision log summary
 * - Filter breakdown for unplaced aircraft
 * - Suggestions for unplaced aircraft
 */

import { Box, Chip, Stack, Typography } from "@mui/material";
import { format } from "date-fns";
import React from "react";
import { TableRow as PlacementRow } from "../utils/buildTableRows";

interface PlacementTableRowExpandedProps {
  row: PlacementRow;
  spotIdToName: Record<string, string>;
}

export const PlacementTableRowExpanded: React.FC<PlacementTableRowExpandedProps> = ({
  row,
  spotIdToName,
}) => {
  // Format time for display
  const formatTime = (date: Date) => {
    return format(date, 'MMM d, HH:mm');
  };

  // Calculate segment duration
  const calculateSegmentDuration = (startBucket: number, endBucket: number): string => {
    const minutes = (endBucket - startBucket) * 15;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
  };

  return (
    <Box
      sx={{
        bgcolor: 'rgba(0,0,0,0.01)',
        borderTop: '1px solid rgba(0,0,0,0.04)',
        px: 2,
        py: 1.5,
      }}
    >
      {/* Placed Aircraft - Show Segments */}
      {row.status !== 'unplaced' && row.segments && row.segments.length >= 1 && (
        <Box sx={{ mb: 1.5 }}>
          <Typography
            sx={{
              fontSize: '10px',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              color: 'text.secondary',
              mb: 1,
            }}
          >
            Segment Breakdown ({row.segments.length} segment{row.segments.length !== 1 ? 's' : ''}, {row.moveCount} move{row.moveCount !== 1 ? 's' : ''})
          </Typography>
          <Stack spacing={0.75}>
            {row.segments.map((segment, idx) => (
              <Box
                key={idx}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  fontSize: '11px',
                  fontFamily: 'SF Mono, Consolas, monospace',
                }}
              >
                <Chip
                  label={`${idx + 1}`}
                  size="small"
                  sx={{
                    height: 18,
                    width: 24,
                    fontSize: '10px',
                    fontWeight: 600,
                    bgcolor: 'rgba(0,0,0,0.05)',
                    '& .MuiChip-label': { px: 0 },
                  }}
                />
                <Typography
                  sx={{
                    fontSize: '11px',
                    fontWeight: 500,
                    color: 'text.primary',
                    fontFamily: 'SF Mono, Consolas, monospace',
                    minWidth: 80,
                  }}
                >
                  {spotIdToName[segment.spotId] || segment.spotId}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '11px',
                    color: 'text.secondary',
                    fontFamily: 'SF Mono, Consolas, monospace',
                  }}
                >
                  {formatTime(new Date(row.arrivalTime.getTime() + segment.startBucket * 15 * 60 * 1000))}
                  {' → '}
                  {formatTime(new Date(row.arrivalTime.getTime() + segment.endBucket * 15 * 60 * 1000))}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '11px',
                    color: 'text.disabled',
                    fontFamily: 'SF Mono, Consolas, monospace',
                    fontStyle: 'italic',
                  }}
                >
                  ({calculateSegmentDuration(segment.startBucket, segment.endBucket)})
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      )}

      {/* Partial Placement - Show Gaps */}
      {row.status === 'partial' && row.partialInfo && (
        <Box sx={{ mb: 1.5 }}>
          <Typography
            sx={{
              fontSize: '10px',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              color: '#F59E0B',
              mb: 1,
            }}
          >
            Partial Placement - {row.partialInfo.gaps.length} Gap{row.partialInfo.gaps.length !== 1 ? 's' : ''}
          </Typography>
          <Stack spacing={0.75}>
            <Box sx={{ display: 'flex', gap: 3, fontSize: '11px' }}>
              <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
                Placed: <strong style={{ color: '#10B981' }}>{Math.round(100 - row.partialInfo.gapPercentage)}%</strong>
              </Typography>
              <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
                Gap: <strong style={{ color: '#EF4444' }}>{Math.round(row.partialInfo.gapPercentage)}%</strong>
              </Typography>
            </Box>
            {row.partialInfo.gaps.map((gap, idx) => (
              <Box
                key={idx}
                sx={{
                  fontSize: '11px',
                  color: 'text.secondary',
                  fontFamily: 'SF Mono, Consolas, monospace',
                  pl: 1,
                }}
              >
                • Gap {idx + 1}: {formatTime(gap.startTime)} → {formatTime(gap.endTime)}
                {' '}
                <Typography
                  component="span"
                  sx={{
                    fontSize: '11px',
                    color: 'text.disabled',
                    fontStyle: 'italic',
                  }}
                >
                  ({Math.round(gap.durationMinutes)}m)
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      )}

      {/* Unplaced Aircraft - Show Reason */}
      {row.status === 'unplaced' && (
        <Box>
          <Typography
            sx={{
              fontSize: '11px',
              color: '#EF4444',
              fontWeight: 500,
            }}
          >
            No available spots found
          </Typography>
          <Typography
            sx={{
              fontSize: '10px',
              color: 'text.secondary',
              mt: 0.5,
            }}
          >
            Check Decision Log tab for detailed filter breakdown
          </Typography>
        </Box>
      )}
    </Box>
  );
};
